import React from 'react';
import PropTypes from 'prop-types';
import Text from 'relements/build/esm/input/text';
import Toggle from 'relements/build/esm/input/toggle';
import Dropdown from 'relements/build/esm/input/dropdown';
import Chips from 'relements/build/esm/input/chips';

import ObjectInput from '@src/components/ObjectInput';
import InfoTooltip from '@src/components/InfoTooltip';

import styles from './PlatformSettings.scss';

export default class PlatformSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentTab: (props.meta.allPlatforms || [])[0].id.toString(),
    };
  }

  setCurrentTab = (option) => {
    this.setState({
      currentTab: option.id.toString(),
    });
  };

  render() {
    const platforms = this.props.meta.allPlatforms || [];
    const selectedDeploymentOption = platforms.find((item) => {
      return item.id.toString() === this.state.currentTab;
    });

    return (
      <div className={styles.platformSettings}>
        <div className={styles.platformSettingsContent}>
          <Dropdown
            options={platforms}
            value={
              selectedDeploymentOption?.name || platforms[0].name.toString()
            }
            onChange={this.setCurrentTab}
            optionKey="name"
            searchKey="name"
            label="Platform"
            className="channels-deployments-dropdown"
            withSearch
          />

          <div
            key={this.state.currentTab}
            className={styles.platformSettingsContentBody}
          >
            {this.renderFields()}
          </div>
        </div>
      </div>
    );
  }

  renderFields = () => {
    const { currentTab } = this.state;
    const { callbackBaseUrl } = this.props;
    const credentials = this.props.value;
    const currentTabCredentials = credentials[currentTab] || {};
    const currentClientCredentials = currentTabCredentials || {};
    const platform = this._getPlatform(currentTab) || {};
    const requiredCredentials = platform.required_credentials || [];

    requiredCredentials.sort((a, b) =>
      // eslint-disable-next-line no-nested-ternary
      a.type === 'BOOLEAN' ? 1 : b.type === 'BOOLEAN' ? -1 : 0
    );
    requiredCredentials.sort((a, b) =>
      // eslint-disable-next-line no-nested-ternary
      a.type === 'ARRAY' ? 1 : b.type === 'ARRAY' ? -1 : 0
    );
    requiredCredentials.sort((a, b) =>
      // eslint-disable-next-line no-nested-ternary
      a.type === 'OBJECT' ? 1 : b.type === 'OBJECT' ? -1 : 0
    );

    return (
      <div className={styles.platformSettingsCredentials}>
        <InfoTooltip>
          <div className={styles.platformSettingsCredentialsInfoTooltip}>
            The deployment settings are not published to production
          </div>
        </InfoTooltip>
        <Toggle
          label="Active"
          className={`${styles.fullWidth} ${styles.extraMargin} ${styles.extraMarginTop} ${styles.platformSettingsInput}`}
          value={currentClientCredentials.active}
          onChange={(value) => this._handleChange('active', value)}
        />
        <Text
          placeholder="Enter..."
          label="Callback URL"
          className={styles.platformSettingsInput}
          value={`${callbackBaseUrl}${currentClientCredentials.callbackURL}`}
          onChange={(value) => this._handleChange('callbackURL', value, true)}
          disabled
        />
        {requiredCredentials.map(
          this.renderCredential(currentClientCredentials)
        )}
      </div>
    );
  };

  renderCredential = (currentClientCredentials) => (credential, i) => {
    let value = currentClientCredentials[credential.name];

    // we get the "token" field name for different platforms in two different key names (depending on which platform)
    // from backend. For some, we get the name "access_token", in others, we get the name "token".
    // Whereas the value we get, is with label "token" always.
    // This check just maps that value to both those labels.
    if (
      credential.name === 'access_token' &&
      !Object.prototype.hasOwnProperty.call(
        currentClientCredentials,
        credential.name
      ) &&
      Object.prototype.hasOwnProperty.call(currentClientCredentials, 'token')
    ) {
      value = currentClientCredentials.token;
    }

    const props = {
      value,
      label: credential.required ? `*${credential.label}` : credential.label,
      className: styles.platformSettingsInput,
      placeholder: 'Enter...',
      options: (credential.options || []).map((title) => ({ title })),
      optionKey: 'title',
      onChange: (newValue) =>
        this._handleChange(credential.name, newValue, credential.readOnly),
      disabled: credential.readOnly,
    };

    switch (credential.type) {
      case 'TEXT':
        return <Text key={i} {...props} />;
      case 'DROPDOWN':
        return <Dropdown key={i} {...props} />;
      case 'OBJECT':
        return <ObjectInput key={i} {...props} />;
      case 'ARRAY':
        return (
          <Chips
            key={i}
            {...props}
            className={`${styles.fullWidth} ${styles.extraMargin} ${styles.platformSettingsInput}`}
            value={props.value || []}
          />
        );
      case 'BOOLEAN':
        return (
          <Toggle
            key={i}
            {...props}
            className={`${styles.platformSettingsInput} ${styles.extraMargin}`}
            value={props.value || false}
          />
        );
      default:
        return <Text key={i} {...props} />;
    }
  };

  _handleChange = (key, value, readOnly = false) => {
    if (readOnly) return;
    const { currentTab } = this.state;

    let deployments = this.props.value;
    if (!deployments[currentTab] || !deployments[currentTab].platformId) {
      deployments = deployments.set(currentTab, {
        platformId: parseInt(currentTab, 10),
        clientId: this._getClientId(this.props.meta.currentPartner),
      });
    }

    let deployment = deployments[currentTab];
    deployment = deployment.set(key, value);
    deployment = deployment.set('edited', true);
    deployment = deployment.set('active', !!deployment.active);
    deployments = deployments.set(currentTab, deployment);

    this.props.onChange(deployments);
  };

  _getPlatform = (platformId = this.state.currentTab) => {
    const platform = (this.props.meta.allPlatforms || []).filter(
      (p) => p.id.toString() === platformId
    )[0];
    let requiredCredentials = [];
    try {
      requiredCredentials = JSON.parse(platform.required_credentials);
    } catch (e) {
      requiredCredentials = [];
    }

    return { ...platform, required_credentials: requiredCredentials };
  };

  _getClientId = (name) => {
    const allClients = this.props.meta.allClients || [];
    return allClients.filter((client) => client.name === name)[0].client_id;
  };
}

PlatformSettings.propTypes = {
  meta: PropTypes.object,
  onChange: PropTypes.func,
  value: PropTypes.object,
  callbackBaseUrl: PropTypes.string,
};

PlatformSettings.defaultProps = {
  meta: {},
  onChange: () => {},
  value: {},
  callbackBaseUrl: '',
};
